<template>
    <Head title="Create Work Order" />

    <div class="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
                <!-- Header -->
                <div class="mb-6">
                    <div class="flex items-center justify-between">
                        <div class="flex items-center gap-3">
                            <div>
                                <h1 class="text-2xl font-semibold text-gray-900 dark:text-white dark:text-white">Create Work Order</h1>
                                <p class="mt-1 text-sm text-gray-600 dark:text-gray-400">Create a new maintenance work order</p>
                            </div>
                            <InfoButton title="Creating Work Orders" variant="default">
                                <p>
                                    <strong class="text-secondary-900 dark:text-white">About this page</strong><br>
                                    The Create Work Order page allows you to initiate maintenance requests for your
                                    organization. Whether it's a corrective repair, preventive maintenance, or
                                    emergency response, this form captures all necessary details to ensure proper
                                    tracking, assignment, and execution. Work orders created here automatically
                                    generate unique tracking numbers and flow into the maintenance workflow system.
                                </p>
                                <p>
                                    <strong class="text-secondary-900 dark:text-white">How to use</strong><br>
                                    • <strong>Title:</strong> Provide a clear, concise summary of the work needed
                                      (e.g., "Replace HVAC filter in Building A")<br>
                                    • <strong>Type:</strong> Select the appropriate maintenance type:
                                      Corrective (repair/fix), Preventive (scheduled), Predictive (data-driven),
                                      Inspection, Calibration, or Emergency<br>
                                    • <strong>Priority:</strong> Set urgency level - Low, Normal, High, or Urgent.
                                      This affects scheduling and assignment<br>
                                    • <strong>Service Category & Service:</strong> Optional cascading selection to
                                      categorize the work. Selecting a category reveals available services with
                                      estimated hours and costs<br>
                                    • <strong>Location:</strong> Specify where the work needs to be performed.
                                      Can be used for location-based work even without an asset<br>
                                    • <strong>Asset:</strong> Link the work order to a specific asset if applicable.
                                      Select a location first to view available assets. Only assets in the selected
                                      location or assets without a location will be shown<br>
                                    • <strong>Description:</strong> Provide detailed information about the issue,
                                      work required, and any relevant context<br>
                                    • <strong>Attachments:</strong> Upload photos, diagrams, or documents (images/PDFs,
                                      max 10MB each) to support the request
                                </p>
                                <p>
                                    <strong class="text-secondary-900 dark:text-white">Tips</strong><br>
                                    • <strong>Be specific in the title</strong> - A clear title helps technicians
                                      quickly understand the work<br>
                                    • <strong>Choose the right type</strong> - Corrective for breakdowns, Preventive
                                      for scheduled maintenance, Emergency for urgent issues<br>
                                    • <strong>Use service categories</strong> when available - They provide
                                      pre-defined workflows and estimated costs<br>
                                    • <strong>Select location first</strong> - The asset dropdown will only show
                                      assets in the selected location, making it easier to find the right asset<br>
                                    • <strong>Link to assets</strong> whenever possible - This builds valuable
                                      maintenance history for reliability tracking<br>
                                    • <strong>Include photos</strong> - Visual documentation helps technicians
                                      understand the issue and prepare proper tools/parts<br>
                                    • <strong>Describe symptoms clearly</strong> - Include when the issue started,
                                      what changed, and any error messages or unusual behavior<br>
                                    • The work order will be created with "Open" status and can be assigned to
                                      technicians by supervisors
                                </p>
                            </InfoButton>
                        </div>
                        <Link
                            :href="route('work-orders.index')"
                            class="inline-flex items-center px-4 py-2 bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-600 rounded-md font-semibold text-xs text-gray-700 dark:text-gray-300 uppercase tracking-widest hover:bg-gray-50 dark:hover:bg-gray-700"
                        >
                            <svg class="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M10 19l-7-7m0 0l7-7m-7 7h18" />
                            </svg>
                            Back
                        </Link>
                    </div>
                </div>

                <!-- Form -->
                <form @submit.prevent="submit" class="bg-white dark:bg-gray-800 rounded-lg shadow border border-gray-200 dark:border-gray-700">
                    <div class="p-6 space-y-6">
                        <!-- Title -->
                        <div>
                            <label for="title" class="block text-sm font-medium text-gray-700 dark:text-gray-300 dark:text-gray-300">
                                Title <span class="text-red-500">*</span>
                            </label>
                            <input
                                id="title"
                                v-model="form.title"
                                type="text"
                                required
                                class="mt-1 block w-full rounded-md border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white shadow-sm focus:border-blue-500 focus:ring-blue-500"
                                :class="{ 'border-red-500': form.errors.title }"
                            />
                            <p v-if="form.errors.title" class="mt-1 text-sm text-red-600 dark:text-red-400">{{ form.errors.title }}</p>
                        </div>

                        <div class="grid grid-cols-1 md:grid-cols-2 gap-6">
                            <!-- Type -->
                            <div>
                                <label for="type" class="block text-sm font-medium text-gray-700 dark:text-gray-300 dark:text-gray-300">
                                    Type <span class="text-red-500">*</span>
                                </label>
                                <select
                                    id="type"
                                    v-model="form.type"
                                    required
                                    class="mt-1 block w-full rounded-md border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white shadow-sm focus:border-blue-500 focus:ring-blue-500"
                                    :class="{ 'border-red-500': form.errors.type }"
                                >
                                    <option value="">Select Type</option>
                                    <option value="corrective">Corrective</option>
                                    <option value="preventive">Preventive</option>
                                    <option value="predictive">Predictive</option>
                                    <option value="inspection">Inspection</option>
                                    <option value="calibration">Calibration</option>
                                    <option value="emergency">Emergency</option>
                                </select>
                                <p v-if="form.errors.type" class="mt-1 text-sm text-red-600 dark:text-red-400">{{ form.errors.type }}</p>
                            </div>

                            <!-- Service (Grouped by Category) -->
                            <div>
                                <label for="service_id" class="block text-sm font-medium text-gray-700 dark:text-gray-300 dark:text-gray-300">
                                    Service
                                </label>
                                <select
                                    id="service_id"
                                    v-model="form.service_id"
                                    @change="onServiceChange"
                                    class="mt-1 block w-full rounded-md border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white shadow-sm focus:border-blue-500 focus:ring-blue-500"
                                    :class="{ 'border-red-500': form.errors.service_id }"
                                >
                                    <option value="">Select Service (Optional)</option>
                                    <optgroup
                                        v-for="category in serviceCategories"
                                        :key="category.id"
                                        :label="category.name"
                                    >
                                        <option
                                            v-for="service in category.services"
                                            :key="service.id"
                                            :value="service.id"
                                        >
                                            {{ service.name }}
                                        </option>
                                    </optgroup>
                                </select>
                                <p v-if="form.errors.service_id" class="mt-1 text-sm text-red-600 dark:text-red-400">{{ form.errors.service_id }}</p>
                            </div>
                        </div>

                        <!-- Priority -->
                        <div>
                            <label for="priority" class="block text-sm font-medium text-gray-700 dark:text-gray-300 dark:text-gray-300">
                                Priority <span class="text-red-500">*</span>
                            </label>
                            <select
                                id="priority"
                                v-model="form.priority"
                                required
                                class="mt-1 block w-full rounded-md border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white shadow-sm focus:border-blue-500 focus:ring-blue-500"
                                :class="{ 'border-red-500': form.errors.priority }"
                            >
                                <option value="">Select Priority</option>
                                <option value="low">Low</option>
                                <option value="normal">Normal</option>
                                <option value="high">High</option>
                                <option value="urgent">Urgent</option>
                            </select>
                            <p v-if="form.errors.priority" class="mt-1 text-sm text-red-600 dark:text-red-400">{{ form.errors.priority }}</p>
                        </div>

                        <div class="grid grid-cols-1 md:grid-cols-2 gap-6">
                            <!-- Location -->
                            <div :class="subscriptionFeatures.asset_module ? '' : 'md:col-span-2'">
                                <label for="location_id" class="block text-sm font-medium text-gray-700 dark:text-gray-300 dark:text-gray-300">
                                    Location
                                </label>
                                <select
                                    id="location_id"
                                    v-model="form.location_id"
                                    @change="onLocationChange"
                                    class="mt-1 block w-full rounded-md border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white shadow-sm focus:border-blue-500 focus:ring-blue-500"
                                    :class="{ 'border-red-500': form.errors.location_id }"
                                >
                                    <option value="">Select Location (Optional)</option>
                                    <option
                                        v-for="location in locations"
                                        :key="location.id"
                                        :value="location.id"
                                    >
                                        {{ location.path_with_type }}
                                    </option>
                                </select>
                                <p v-if="form.errors.location_id" class="mt-1 text-sm text-red-600 dark:text-red-400">{{ form.errors.location_id }}</p>
                            </div>

                            <!-- Asset -->
                            <div v-if="subscriptionFeatures.asset_module">
                                <label for="asset_id" class="block text-sm font-medium text-gray-700 dark:text-gray-300 dark:text-gray-300">
                                    Asset
                                </label>
                                <select
                                    id="asset_id"
                                    v-model="form.asset_id"
                                    :disabled="!form.location_id"
                                    class="mt-1 block w-full rounded-md border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white shadow-sm focus:border-blue-500 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed"
                                    :class="{ 'border-red-500': form.errors.asset_id }"
                                >
                                    <option value="">Select Asset (Optional)</option>
                                    <option
                                        v-for="asset in filteredAssets"
                                        :key="asset.id"
                                        :value="asset.id"
                                    >
                                        {{ asset.name }} ({{ asset.asset_tag }})
                                    </option>
                                </select>
                                <p v-if="!form.location_id" class="mt-1 text-xs text-gray-500 dark:text-gray-400">
                                    Select location to view assets
                                </p>
                                <p v-if="form.errors.asset_id" class="mt-1 text-sm text-red-600 dark:text-red-400">{{ form.errors.asset_id }}</p>
                            </div>
                        </div>

                        <!-- Description -->
                        <div>
                            <label for="description" class="block text-sm font-medium text-gray-700 dark:text-gray-300 dark:text-gray-300">
                                Description <span class="text-red-500">*</span>
                            </label>
                            <textarea
                                id="description"
                                v-model="form.description"
                                rows="4"
                                required
                                class="mt-1 block w-full rounded-md border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white shadow-sm focus:border-blue-500 focus:ring-blue-500"
                                :class="{ 'border-red-500': form.errors.description }"
                            ></textarea>
                            <p v-if="form.errors.description" class="mt-1 text-sm text-red-600 dark:text-red-400">{{ form.errors.description }}</p>
                        </div>

                        <!-- File Attachments -->
                        <div>
                            <label class="block text-sm font-medium text-gray-700 dark:text-gray-300 dark:text-gray-300 mb-2">
                                Attachments (Images/PDFs)
                            </label>

                            <!-- Document Upload Enabled -->
                            <div v-if="canUploadDocuments">
                                <div class="flex items-center justify-center w-full">
                                    <label
                                        for="attachments"
                                        class="flex flex-col items-center justify-center w-full h-32 border-2 border-gray-300 dark:border-gray-600 border-dashed rounded-lg cursor-pointer bg-gray-50 dark:bg-gray-700/50 hover:bg-gray-100 dark:hover:bg-gray-700"
                                    >
                                        <div class="flex flex-col items-center justify-center pt-5 pb-6">
                                            <svg class="w-8 h-8 mb-3 text-gray-400 dark:text-gray-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12" />
                                            </svg>
                                            <p class="mb-2 text-sm text-gray-500 dark:text-gray-400">
                                                <span class="font-semibold">Click to upload</span> or drag and drop
                                            </p>
                                            <p class="text-xs text-gray-500 dark:text-gray-400">Images (PNG, JPG) or PDF files (Max 10MB each)</p>
                                        </div>
                                        <input
                                            id="attachments"
                                            type="file"
                                            multiple
                                            accept="image/jpeg,image/jpg,image/png,application/pdf"
                                            @change="onFileChange"
                                            class="hidden"
                                        />
                                    </label>
                                </div>

                                <!-- File Preview -->
                                <div v-if="selectedFiles.length > 0" class="mt-4 space-y-2">
                                    <div
                                        v-for="(file, index) in selectedFiles"
                                        :key="index"
                                        class="flex items-center justify-between p-3 bg-gray-50 dark:bg-gray-700/50 rounded-lg border border-gray-200 dark:border-gray-600"
                                    >
                                        <div class="flex items-center space-x-3">
                                            <svg v-if="file.type.startsWith('image/')" class="w-5 h-5 text-blue-500 dark:text-blue-400" fill="currentColor" viewBox="0 0 20 20">
                                                <path fill-rule="evenodd" d="M4 3a2 2 0 00-2 2v10a2 2 0 002 2h12a2 2 0 002-2V5a2 2 0 00-2-2H4zm12 12H4l4-8 3 6 2-4 3 6z" clip-rule="evenodd" />
                                            </svg>
                                            <svg v-else class="w-5 h-5 text-red-500 dark:text-red-400" fill="currentColor" viewBox="0 0 20 20">
                                                <path fill-rule="evenodd" d="M4 4a2 2 0 012-2h4.586A2 2 0 0112 2.586L15.414 6A2 2 0 0116 7.414V16a2 2 0 01-2 2H6a2 2 0 01-2-2V4z" clip-rule="evenodd" />
                                            </svg>
                                            <div>
                                                <p class="text-sm font-medium text-gray-900 dark:text-white">{{ file.name }}</p>
                                                <p class="text-xs text-gray-500 dark:text-gray-400">{{ formatFileSize(file.size) }}</p>
                                            </div>
                                        </div>
                                        <button
                                            type="button"
                                            @click="removeFile(index)"
                                            class="text-red-600 dark:text-red-400 hover:text-red-800 dark:hover:text-red-300"
                                        >
                                            <svg class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12" />
                                            </svg>
                                        </button>
                                    </div>
                                </div>
                            </div>

                            <!-- Document Upload Disabled - Show Upgrade Message -->
                            <div v-else class="flex items-center justify-center w-full">
                                <div class="w-full p-6 border-2 border-amber-200 dark:border-amber-800 bg-amber-50 dark:bg-amber-900/20 rounded-lg">
                                    <div class="flex items-start space-x-3">
                                        <svg class="w-6 h-6 text-amber-600 dark:text-amber-500 flex-shrink-0 mt-0.5" fill="currentColor" viewBox="0 0 20 20">
                                            <path fill-rule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clip-rule="evenodd" />
                                        </svg>
                                        <div class="flex-1">
                                            <h3 class="text-sm font-semibold text-amber-900 dark:text-amber-200 mb-1">Document Upload Not Available</h3>
                                            <p class="text-sm text-amber-800 dark:text-amber-300">
                                                Document upload is not included in your company's subscription plan.
                                                Contact your company administrator for more information about upgrading your plan.
                                            </p>
                                        </div>
                                    </div>
                                </div>
                            </div>

                            <p v-if="form.errors.attachments" class="mt-1 text-sm text-red-600 dark:text-red-400">{{ form.errors.attachments }}</p>
                        </div>
                    </div>

                    <!-- Actions -->
                    <div class="bg-gray-50 dark:bg-gray-700/50 px-6 py-4 flex items-center justify-end space-x-3 border-t border-gray-200 dark:border-gray-600">
                        <Link
                            :href="route('work-orders.index')"
                            class="inline-flex items-center px-4 py-2 bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded-md font-semibold text-xs text-gray-700 dark:text-gray-300 uppercase tracking-widest hover:bg-gray-50 dark:hover:bg-gray-600"
                        >
                            Cancel
                        </Link>
                        <button
                            type="submit"
                            :disabled="form.processing"
                            class="inline-flex items-center px-4 py-2 bg-blue-600 dark:bg-blue-500 border border-transparent rounded-md font-semibold text-xs text-white uppercase tracking-widest hover:bg-blue-700 dark:hover:bg-blue-600 disabled:opacity-50"
                        >
                            <svg v-if="form.processing" class="animate-spin -ml-1 mr-2 h-4 w-4 text-white" fill="none" viewBox="0 0 24 24">
                                <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
                                <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                            </svg>
                            Create Work Order
                        </button>
                    </div>
                </form>
            </div>
</template>

<script setup>
import { computed, ref } from 'vue';
import { Head, Link, usePage } from '@inertiajs/vue3';
import { useToast } from '@/Composables/useToast';
import { useCurrency } from '@/Composables/useCurrency';
import { useSecureForm } from '@/Composables/useSecureForm';
import route from '@/Utils/route';
import InfoButton from '@/Components/Common/InfoButton.vue';

// Access subscription features from Inertia shared props
const page = usePage();
const subscriptionFeatures = computed(() => page.props.subscriptionFeatures || { asset_module: false, equipment_module: false });

const props = defineProps({
    assets: Array,
    locations: Array,
    technicians: Array,
    serviceCategories: Array,
    prefilledData: Object,
    canUploadDocuments: {
        type: Boolean,
        default: false,
    },
    companyOwner: {
        type: Object,
        default: null,
    },
});

const toast = useToast();
const { formatMoney } = useCurrency();

const form = useSecureForm({
    title: props.prefilledData?.title || '',
    description: '',
    type: '',
    service_id: '',
    priority: 'normal',
    asset_id: props.prefilledData?.asset_id || '',
    location_id: props.prefilledData?.location_id || '',
    attachments: [],
});

const selectedFiles = ref([]);

// Filter assets based on selected location
const filteredAssets = computed(() => {
    if (!form.location_id) {
        return [];
    }

    // Show assets that are either:
    // 1. In the selected location
    // 2. Have no location assigned (location_id is null)
    return props.assets.filter(asset =>
        asset.location_id === form.location_id || asset.location_id === null
    );
});

// Handle location change - clear asset if it's not in the filtered list
const onLocationChange = () => {
    if (!form.location_id) {
        // Location cleared, reset asset
        form.asset_id = '';
        return;
    }

    // Check if currently selected asset is still valid for the new location
    if (form.asset_id) {
        const isAssetValid = filteredAssets.value.some(asset => asset.id === form.asset_id);
        if (!isAssetValid) {
            // Clear asset selection if it's not available for the selected location
            form.asset_id = '';
        }
    }
};

// When service changes, auto-populate priority from service's default_priority
const onServiceChange = () => {
    if (!form.service_id) {
        return;
    }

    // Find the selected service from all categories
    let selectedService = null;
    for (const category of props.serviceCategories) {
        selectedService = category.services.find(s => s.id === form.service_id);
        if (selectedService) {
            break;
        }
    }

    // Auto-populate priority if service has a default_priority
    if (selectedService && selectedService.default_priority) {
        form.priority = selectedService.default_priority;
    }
};

// Handle file selection
const onFileChange = (event) => {
    const files = Array.from(event.target.files);

    // Validate file size (10MB max)
    const maxSize = 10 * 1024 * 1024; // 10MB in bytes
    const validFiles = files.filter(file => {
        if (file.size > maxSize) {
            toast.error(`File ${file.name} is too large. Maximum size is 10MB.`);
            return false;
        }
        return true;
    });

    selectedFiles.value = [...selectedFiles.value, ...validFiles];
    form.attachments = selectedFiles.value;

    // Reset input
    event.target.value = '';
};

// Remove file from selection
const removeFile = (index) => {
    selectedFiles.value.splice(index, 1);
    form.attachments = selectedFiles.value;
};

// Format file size for display
const formatFileSize = (bytes) => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return Math.round(bytes / Math.pow(k, i) * 100) / 100 + ' ' + sizes[i];
};

const submit = () => {
    form.post(route('work-orders.store'), {
        onSuccess: () => {
            toast.success('Work order created successfully');
        },
        // Let Inertia handle errors automatically - no manual toast needed
        // This prevents duplicate error messages from global error handler
    });
};
</script>
